import React from 'react';
import {
    Box,
    Typography,
    Avatar,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Chip,
    Divider,
    useTheme,
    Paper,
    Collapse,
    IconButton,
    Tooltip,
    List
} from '@mui/material';
import TimelineIcon from '@mui/icons-material/Timeline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { OrderEvent } from '../../../interfaces/delivery-company/orders';
import { parseOrderEventChanges, parseOrderEventActor } from '../../../utils/orderevent';

interface EventItemProps {
    event: OrderEvent;
    isLast: boolean;
}

const EventItem: React.FC<EventItemProps> = ({ event, isLast }) => {
    const theme = useTheme();
    const [expanded, setExpanded] = React.useState(false);
    const changes = parseOrderEventChanges(event);
    const actor = parseOrderEventActor(event);

    const getEventColor = () => {
        if (event.changes.delivery_state) return theme.palette.primary.main;
        if (event.changes.payment_state) return theme.palette.secondary.main;
        if (event.changes.merchant_invoice || event.changes.driver_invoice) return theme.palette.success.main;
        return theme.palette.grey[500];
    };

    return (
        <>
            <ListItem sx={{
                alignItems: 'flex-start',
                px: 0,
                py: 2,
                '&:hover': {
                    backgroundColor: theme.palette.action.hover
                }
            }}>
                <ListItemAvatar sx={{ minWidth: 40 }}>
                    <Avatar sx={{
                        bgcolor: getEventColor(),
                        color: theme.palette.getContrastText(getEventColor()),
                        width: 32,
                        height: 32
                    }}>
                        <TimelineIcon fontSize="small" />
                    </Avatar>
                </ListItemAvatar>
                <ListItemText
                    primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption" color="text.secondary">
                                {new Date(event.timestamp).toLocaleString()}
                            </Typography>
                            <Chip
                                label={actor.split(':')[0]} // Show just the role (e.g., "Admin")
                                size="small"
                                sx={{
                                    backgroundColor: theme.palette.grey[200],
                                    color: theme.palette.text.primary
                                }}
                                title={actor} // Full info on hover
                            />
                        </Box>
                    }
                    secondary={
                        <Box sx={{ mt: 0.5 }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>{actor.split(':')[1].trim()}</strong> {/* Show the name and phone */}
                            </Typography>

                            {changes.length > 0 && (
                                <>


                                    <Box sx={{ position: 'relative', pl: 2, mt: 1 }}>

                                        {changes.slice(0, expanded ? changes.length : 2).map((change, index) => (
                                            <React.Fragment key={index}>
                                                <Box sx={{
                                                    display: 'flex',
                                                    alignItems: 'flex-start',
                                                    justifyItems: "center",
                                                    pb: index === changes.length - 1 ? 0 : 1.5,
                                                    position: 'relative',

                                                    minHeight: 24 // Ensures consistent row height
                                                }}>
                                                    {/* Bullet point */}
                                                    <Box sx={{
                                                        position: 'absolute',
                                                        left: 0,
                                                        top: '6px', // Adjusted to align with first line of text
                                                        width: 12,
                                                        height: 12,
                                                        borderRadius: '50%',
                                                        backgroundColor: 'primary.main',
                                                        border: `2px solid ${theme.palette.background.paper}`,
                                                    }} />

                                                    {/* Vertical connector */}
                                                    {index !== changes.length - 1 && (
                                                        <Box sx={{
                                                            position: 'absolute',
                                                            left: 5, // Centered under bullet
                                                            top: 18, // Starts right below bullet
                                                            bottom: 0,
                                                            width: '2px',
                                                            backgroundColor: 'divider'
                                                        }} />
                                                    )}

                                                    {/* Text */}
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            pl: 3, // Increased padding to account for bullet
                                                            lineHeight: 1.5,
                                                            mt: '1px' // Tiny adjustment for perfect alignment
                                                        }}
                                                    >
                                                        {change}
                                                    </Typography>
                                                </Box>
                                            </React.Fragment>
                                        ))}
                                    </Box>
                                    {changes.length > 2 && (
                                        <IconButton
                                            size="small"
                                            onClick={() => setExpanded(!expanded)}
                                            sx={{ mt: 0.5 }}
                                        >
                                            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                            <Typography variant="caption" sx={{ ml: 0.5 }}>
                                                {expanded ? 'Show less' : `Show all (${changes.length})`}
                                            </Typography>
                                        </IconButton>
                                    )}
                                </>
                            )}
                            {event.reason && (
                                <Paper
                                    elevation={0}
                                    sx={{
                                        mt: 1,
                                        p: 1,
                                        backgroundColor: theme.palette.grey[100],
                                        borderRadius: theme.shape.borderRadius
                                    }}
                                >
                                    <Typography variant="body2">
                                        <strong>Remarks:</strong> {event.reason}
                                    </Typography>
                                </Paper>
                            )}
                        </Box>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                />
            </ListItem>
            {!isLast && <Divider variant="inset" component="li" />}
        </>
    );
};

interface EnhancedEventsTimelineProps {
    events?: OrderEvent[];
    color: string;
}

export const EnhancedEventsTimeline: React.FC<EnhancedEventsTimelineProps> = ({
    events,
    color
}) => {
    const theme = useTheme();

    return (
        <Paper elevation={3} sx={{
            borderRadius: theme.shape.borderRadius,
            overflow: 'hidden',
            mb: 2
        }}>
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1,
                backgroundColor: color,
                color: theme.palette.getContrastText(color),
            }}>
                <TimelineIcon fontSize="small" />
                <Typography variant="subtitle1" fontWeight="bold">
                    Order Events
                </Typography>
            </Box>
            <Box sx={{ p: 1, maxHeight: 500, overflow: 'auto' }}>
                {events?.filter(event => parseOrderEventChanges(event).length > 0).length ? (
                    <List sx={{ py: 0 }}>
                        {events
                            ?.filter(event => parseOrderEventChanges(event).length > 0)
                            .map((event, index, array) => (
                                <EventItem
                                    key={event.timestamp}
                                    event={event}
                                    isLast={index === array.length - 1}
                                />
                            ))}
                    </List>
                ) : (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            No events recorded yet
                        </Typography>
                    </Box>
                )}
            </Box>
        </Paper>
    );
};